import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Truck, RefreshCw, ShieldCheck, Sparkles } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import SparkleEffect from '../components/SparkleEffect';
import { useShopify } from '../context/ShopifyContext';
import { toast } from '../hooks/use-toast';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 }
};

// Loading skeleton component
const ProductSkeleton = () => (
  <div className="animate-pulse">
    <div className="aspect-[3/4] rounded-2xl bg-gradient-to-br from-pink-100 to-rose-100" />
    <div className="mt-4 space-y-2">
      <div className="h-5 bg-pink-100 rounded w-3/4" />
      <div className="h-4 bg-pink-50 rounded w-1/2" />
    </div>
  </div>
);

const HomePage = () => {
  const { 
    products, 
    productsLoading, 
    addToCart,
  } = useShopify();

  // Helper function to find the best product image for a category
  const findCategoryImage = (category) => {
    // Priority 1: Match specific keywords first for accurate thumbnails
    if (category.imageKeywords) {
      for (const keyword of category.imageKeywords) {
        const found = products.find(p => {
          const title = (p.title || '').toLowerCase();
          return title.includes(keyword.toLowerCase());
        });
        if (found?.featuredImage?.url) {
          return found.featuredImage.url;
        }
      }
    }
    
    // Priority 2: Match by productType
    if (category.productTypes) {
      for (const pType of category.productTypes) {
        const found = products.find(p => {
          const productType = (p.productType || '').toLowerCase();
          const title = (p.title || '').toLowerCase();
          
          // For sweaters, only match tops that have sweater keywords
          if (category.handle === 'sweaters') {
            return productType === 'tops' && 
                   (title.includes('sweater') || title.includes('knit') || title.includes('cozy'));
          }
          
          // For tops, exclude sweaters
          if (category.handle === 'tops') {
            return (productType === 'tops' || productType === 'bodysuit' || productType === 'jackets') &&
                   !title.includes('sweater') && !title.includes('knit') && !title.includes('cozy');
          }
          
          // For jewelry, only match accessories with jewelry keywords
          if (category.handle === 'jewelry') {
            return productType === 'accessories' && 
                   (title.includes('necklace') || title.includes('bracelet') || 
                    title.includes('earring') || title.includes('hoops') || title.includes('drops'));
          }
          
          // For non-jewelry accessories
          if (category.handle === 'accessories') {
            return productType === 'accessories' && 
                   !title.includes('necklace') && !title.includes('bracelet') && 
                   !title.includes('earring') && !title.includes('hoops') && !title.includes('drops');
          }
          
          return productType === pType.toLowerCase();
        });
        if (found?.featuredImage?.url) {
          return found.featuredImage.url;
        }
      }
    }
    
    return null;
  };

  // Get category image with fallback
  const getCategoryImage = (category) => {
    const productImage = findCategoryImage(category);
    return productImage || category.fallback;
  };

  // All categories matching Shopify productTypes
  // Each category has: name, handle (URL), productTypes to match, imageKeywords for best thumbnail, fallback image
  const categories = [
    { 
      name: 'Dresses', 
      handle: 'dresses', 
      productTypes: ['dresses'],
      imageKeywords: ['rosewood', 'blush breeze', 'sunset pop'],
      fallback: 'https://cdn.shopify.com/s/files/1/0808/2476/2588/files/IMG-7371.png?v=1770922303'
    },
    { 
      name: 'Tops & Bodysuits', 
      handle: 'tops', 
      productTypes: ['tops', 'bodysuit', 'jackets'],
      imageKeywords: ['bodysuit', 'tank', 'jacket'],
      fallback: 'https://cdn.shopify.com/s/files/1/0808/2476/2588/files/IMG-6919.png?v=1768514397'
    },
    { 
      name: 'Sets & Loungewear', 
      handle: 'sets', 
      productTypes: ['2-piece sets', 'pants', 'active sets'],
      imageKeywords: ['cardigan & shorts', 'puff sleeve', 'lounge', 'jogger', 'cherry pop'],
      fallback: 'https://cdn.shopify.com/s/files/1/0808/2476/2588/files/FullSizeRender_b030a349-65ff-4395-8676-941d8ceb7e4b.jpg?v=1768514393'
    },
    { 
      name: 'Sunglasses', 
      handle: 'sunglasses', 
      productTypes: [],
      imageKeywords: ['sunglasses', 'fashion sunglasses', 'black frame'],
      fallback: 'https://cdn.shopify.com/s/files/1/0808/2476/2588/files/IMG-7269.png?v=1770305434'
    },
    { 
      name: 'Jewelry', 
      handle: 'jewelry', 
      productTypes: ['accessories'],
      imageKeywords: ['necklace', 'bracelet', 'golden romance'],
      fallback: 'https://cdn.shopify.com/s/files/1/0808/2476/2588/files/IMG-7354.png?v=1770922303'
    },
    { 
      name: 'Shorts & Jeans', 
      handle: 'shorts', 
      productTypes: ['judy blue jeans'],
      imageKeywords: ['judy blue', 'shorts', 'distressed'],
      fallback: 'https://cdn.shopify.com/s/files/1/0808/2476/2588/files/IMG-7269.png?v=1770305434'
    },
  ];

  // Featured products (first 8)
  const featuredProducts = products.slice(0, 8);

  // Pink-related products for "On Trend in Pink" section
  const pinkProducts = products.filter(p => {
    const title = (p.title || '').toLowerCase();
    const tags = (p.tags || []).map(t => t.toLowerCase());
    const description = (p.description || '').toLowerCase();
    
    // Match products with pink in title, tags, or description
    const hasPinkInTitle = title.includes('pink') || title.includes('rose') || 
                           title.includes('blush') || title.includes('fuchsia') ||
                           title.includes('coral') || title.includes('cherry');
    const hasPinkInTags = tags.some(t => t.includes('pink') || t.includes('rose') || 
                                         t.includes('blush') || t.includes('fuchsia'));
    const hasPinkInDescription = description.includes('pink') || description.includes('rose') ||
                                  description.includes('blush');
    
    return hasPinkInTitle || hasPinkInTags || hasPinkInDescription;
  });

  const handleAddToCart = async (product, variant) => {
    try {
      await addToCart(product, variant);
      toast({
        title: "Added to cart!",
        description: `${product.title} has been added to your cart.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add item to cart. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <motion.section 
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
        className="relative h-[550px] md:h-[650px] overflow-hidden"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://thepinkedit.net/cdn/shop/files/pink-clothes-on-rack.png)'
          }}
        >
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-pink-900/80 via-pink-900/60 to-transparent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          />
        </div>
        
        <SparkleEffect count={12} color="#f9a8d4" className="z-10" />
        
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <motion.div 
            className="max-w-2xl text-white"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-4">
              <motion.div
                animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.3, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
              >
                <Sparkles className="text-pink-300" size={24} />
              </motion.div>
              <span className="text-pink-200 font-medium tracking-wide">THE PINK EDIT • ARIZONA</span>
              <motion.div
                animate={{ rotate: [0, -15, 15, 0], scale: [1, 1.3, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1.5 }}
              >
                <Sparkles className="text-pink-300" size={18} />
              </motion.div>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-7xl font-bold mb-6 leading-tight" 
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Discover Our Soft Blush Pink Collection
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl md:text-2xl mb-8 text-pink-100 italic" 
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Embrace elegance and femininity with our curated selection of soft blush pink pieces. Perfect for every occasion.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/collections/all"
                  data-testid="hero-shop-now-btn"
                  className="relative px-8 py-4 bg-white text-pink-600 rounded-full font-semibold hover:bg-pink-50 transition-all shadow-xl hover:shadow-2xl flex items-center justify-center space-x-2 sparkle-border"
                >
                  <Sparkles size={16} className="text-pink-400" />
                  <span>Shop Now</span>
                  <ArrowRight size={20} />
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Trust Badges */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="bg-white py-12 border-b border-pink-100"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Truck, title: 'Free Shipping', desc: 'On orders over $50' },
              { icon: RefreshCw, title: 'Easy Returns', desc: '7-day return policy' },
              { icon: ShieldCheck, title: 'Secure Checkout', desc: '100% secure payments' }
            ].map((item, index) => (
              <motion.div 
                key={index}
                variants={scaleIn}
                className="flex items-center space-x-4 group"
              >
                <motion.div 
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="p-4 bg-gradient-to-br from-pink-100 to-rose-100 rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow relative sparkle-border"
                >
                  <item.icon className="text-pink-600" size={28} />
                  <motion.div
                    className="absolute -top-1 -right-1"
                    animate={{ scale: [0, 1, 0], rotate: [0, 180, 360] }}
                    transition={{ duration: 3, delay: index * 0.5, repeat: Infinity }}
                  >
                    <Sparkles size={10} className="text-pink-400" />
                  </motion.div>
                </motion.div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Shop by Category */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        className="py-20 bg-gradient-to-b from-white via-pink-50/50 to-white relative"
      >
        <SparkleEffect count={10} color="#f472b6" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <motion.div
                animate={{ rotate: [0, 360], scale: [1, 1.2, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="text-pink-400" size={22} />
              </motion.div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Shop by Category
              </h2>
              <motion.div
                animate={{ rotate: [360, 0], scale: [1, 1.2, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="text-pink-400" size={22} />
              </motion.div>
            </div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Find exactly what you're looking for
            </p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.handle}
                variants={scaleIn}
                custom={index}
              >
                <Link
                  to={`/collections/${category.handle}`}
                  data-testid={`category-${category.handle}`}
                  className="group relative h-64 md:h-72 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 block sparkle-border"
                >
                  <motion.img 
                    src={getCategoryImage(category)}
                    alt={category.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover:from-black/70 transition-all duration-500" />
                  
                  {/* Sparkle overlay on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    {[...Array(4)].map((_, si) => (
                      <motion.div
                        key={si}
                        className="absolute"
                        style={{ left: `${20 + si * 20}%`, top: `${15 + si * 15}%` }}
                        animate={{
                          scale: [0, 1, 0],
                          opacity: [0, 0.8, 0],
                          rotate: [0, 180, 360],
                        }}
                        transition={{
                          duration: 2,
                          delay: si * 0.3,
                          repeat: Infinity,
                        }}
                      >
                        <Sparkles size={14} className="text-white/70" />
                      </motion.div>
                    ))}
                  </div>
                  
                  <div className="absolute inset-0 flex items-end p-6">
                    <motion.div 
                      className="text-white"
                      initial={{ y: 10, opacity: 0.8 }}
                      whileHover={{ y: 0, opacity: 1 }}
                    >
                      <h3 className="text-2xl font-bold mb-1 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {category.name}
                        <motion.div
                          animate={{ scale: [1, 1.3, 1], rotate: [0, 15, -15, 0] }}
                          transition={{ duration: 2.5, repeat: Infinity }}
                        >
                          <Sparkles size={16} className="text-pink-200" />
                        </motion.div>
                      </h3>
                      <p className="text-sm opacity-90 flex items-center gap-2 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        Shop Now
                        <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform" />
                      </p>
                    </motion.div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Featured Products */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        className="py-20 bg-white relative"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="flex justify-between items-center mb-12">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Featured Products
                </h2>
                <motion.div
                  animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 0.5 }}
                >
                  <Sparkles className="text-pink-500" size={26} />
                </motion.div>
              </div>
              <p className="text-xl text-gray-600 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Our curated selection of pink essentials
              </p>
            </div>
            <motion.div whileHover={{ x: 5 }}>
              <Link
                to="/collections/all"
                className="text-pink-600 font-semibold hover:text-pink-700 flex items-center space-x-2 group"
              >
                <span>View All</span>
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
          
          {productsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => <ProductSkeleton key={i} />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product, index) => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  onAddToCart={handleAddToCart}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </motion.section>

      {/* On Trend Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        className="py-20 bg-gradient-to-br from-pink-50 via-rose-50 to-pink-50 overflow-hidden relative"
      >
        <SparkleEffect count={15} color="#f9a8d4" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeInUp} className="space-y-6">
              <h2 className="text-4xl md:text-6xl font-bold text-gray-900 flex items-start gap-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                <span>On Trend in Pink</span>
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                  className="flex-shrink-0 mt-2"
                >
                  <Sparkles className="text-pink-500" size={28} />
                </motion.div>
              </h2>
              <p className="text-xl text-gray-600 italic leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                The Pink Edit brings you thoughtfully selected styles in soft pinks and bold statements, made to celebrate confidence, femininity, and everyday beauty.
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/collections/all"
                  data-testid="on-trend-shop-now-btn"
                  className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full font-semibold hover:from-pink-600 hover:to-rose-600 transition-all shadow-lg hover:shadow-xl sparkle-border"
                >
                  <Sparkles size={16} />
                  <span>Shop Now</span>
                  <ArrowRight size={20} />
                </Link>
              </motion.div>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4">
              {pinkProducts.length > 0 ? (
                <>
                  <motion.img
                    whileHover={{ scale: 1.05, rotate: 2 }}
                    src={pinkProducts[0]?.featuredImage?.url || "https://cdn.shopify.com/s/files/1/0808/2476/2588/files/IMG-7371.png"}
                    alt={pinkProducts[0]?.title || "Pink fashion"}
                    className="rounded-2xl shadow-xl w-full h-64 object-cover"
                  />
                  <motion.img
                    whileHover={{ scale: 1.05, rotate: -2 }}
                    src={pinkProducts[1]?.featuredImage?.url || pinkProducts[0]?.featuredImage?.url || "https://cdn.shopify.com/s/files/1/0808/2476/2588/files/IMG-7354.png"}
                    alt={pinkProducts[1]?.title || "Pink clothes"}
                    className="rounded-2xl shadow-xl w-full h-64 object-cover mt-8"
                  />
                </>
              ) : (
                <>
                  <motion.img
                    whileHover={{ scale: 1.05, rotate: 2 }}
                    src="https://cdn.shopify.com/s/files/1/0808/2476/2588/files/IMG-7371.png"
                    alt="Pink fashion"
                    className="rounded-2xl shadow-xl w-full h-64 object-cover"
                  />
                  <motion.img
                    whileHover={{ scale: 1.05, rotate: -2 }}
                    src="https://cdn.shopify.com/s/files/1/0808/2476/2588/files/IMG-7354.png"
                    alt="Pink clothes"
                    className="rounded-2xl shadow-xl w-full h-64 object-cover mt-8"
                  />
                </>
              )}
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Testimonials */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="py-20 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <div className="flex items-center justify-center gap-3">
              <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                <Sparkles className="text-pink-400" size={20} />
              </motion.div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                What Our Customers Say
              </h2>
              <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}>
                <Sparkles className="text-pink-400" size={20} />
              </motion.div>
            </div>
            <p className="text-gray-600 text-lg">Real reviews from real pink lovers</p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah M.',
                location: 'Phoenix, AZ',
                review: 'Absolutely love the quality and fit! The sweaters are so soft and the pink shades are perfect.',
                rating: 5
              },
              {
                name: 'Emily R.',
                location: 'Scottsdale, AZ',
                review: 'Best online shopping experience! Fast shipping and the pieces are even more beautiful in person.',
                rating: 5
              },
              {
                name: 'Jessica L.',
                location: 'Tucson, AZ',
                review: 'The Pink Edit has become my go-to for feminine, quality pieces. Highly recommend!',
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div 
                key={index}
                variants={scaleIn}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-gradient-to-br from-pink-50 to-rose-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <Star size={20} className="text-yellow-400 fill-yellow-400" />
                    </motion.div>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  "{testimonial.review}"
                </p>
                <p className="font-semibold text-gray-900">{testimonial.name}</p>
                <p className="text-sm text-pink-600">{testimonial.location}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Final CTA */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="relative py-28 overflow-hidden"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://thepinkedit.net/cdn/shop/files/soft-blush-pink-hero-image_d4498a9d-ac0e-46c4-86a0-02735606d614.png)'
          }}
        >
          <motion.div 
            className="absolute inset-0 bg-gradient-to-br from-pink-900/80 to-rose-900/70"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1 }}
          />
        </div>
        
        <SparkleEffect count={20} color="#fce7f3" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div className="flex justify-center gap-4 mb-6">
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                animate={{ 
                  y: [0, -10, 0], 
                  rotate: [0, 15, -15, 0],
                  scale: [1, 1.3, 1]
                }}
                transition={{ duration: 2, delay: i * 0.3, repeat: Infinity }}
              >
                <Sparkles className="text-pink-300" size={20} />
              </motion.div>
            ))}
          </motion.div>
          
          <motion.h2 
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold mb-8 italic" 
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Pretty things for powerful women
          </motion.h2>
          
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/collections/all"
              data-testid="cta-shop-now-btn"
              className="inline-flex items-center space-x-2 px-12 py-5 bg-white text-pink-600 rounded-full font-bold text-lg hover:bg-pink-50 transition-all shadow-2xl sparkle-border"
            >
              <Sparkles size={18} />
              <span>Shop now</span>
              <ArrowRight size={24} />
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default HomePage;
